import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { uniqueId } from "lodash";
import * as Yup from "yup";
import { Box, Button, Grid, Typography, CircularProgress } from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import AddIcon from "@mui/icons-material/Add";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { FIRE_EMERGENCY_CHIEF_FIRE_OFFICER } from "common/constant";
import { DocumentName } from "pages/UtilityConsumers/style";
import Tooltip from "Components/Common/Tooltip";

import {
  filterEmptyStrings,
  generateFormData,
  getImageUrl,
  isAuthorizedUser,
} from "utils";
import getFileExtension from "utils/getFileExtension";

import {
  useGetObserverTypeQuery,
  useLazyFireSafetyBuildingObservationQuery,
  useLazyFireSafetyObservationDataQuery,
  usePostFireSafetyBuildingObservationMutation,
} from "apis/FireSafety/FireSafetyObservation/fireSafetyObservation";
import { useGetAllTenantsQuery } from "apis/FireSafety/FireSafetyTenantDetail";

import pdfImg from "assets/images/pdf.png";
import { UploadImage } from "./style";

const validFileFormats = ["jpg", "png", "jpeg"];

const otherObservationValue = "other";

const FireSafetyBuildingObservation = ({
  applicationId,
  scheduleId,
  handleClose,
  t,
  isObservationNoticeSend,
}) => {
  const { data: { data: observationTypeOptions } = {} } =
    useGetObserverTypeQuery();

  const [addBuildingObservation, { isLoading }] =
    usePostFireSafetyBuildingObservationMutation();

  const [
    getBuildingObservationDataById,
    {
      isLoading: isBuildingObservationDataLoading,
      isFetching: isBuildingObservationDataFetching,
      data: { data: buildingObservationData } = [],
    },
  ] = useLazyFireSafetyBuildingObservationQuery();

  const [
    getDetailObservationOptionById,
    {
      isLoading: isBuildingDetailObservationDataLoading,
      isFetching: isBuildingDetailObservationDataFetching,
      data: { data: buildingObservationOptionData } = [],
    },
  ] = useLazyFireSafetyObservationDataQuery();

  const { data: { data: tenantOptions = [] } = {} } =
    useGetAllTenantsQuery(applicationId);

  const { roles } = useSelector((state) => state.Login);

  const ref = useRef();
  const [isAddBtnClicked, setIsAddBtnClicked] = useState(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] = useState(false);

  const updatedObservationTypeOptions = useMemo(
    () => [
      ...(observationTypeOptions?.map(({ id, name }) => ({
        id,
        label: name,
        value: id,
      })) ?? []),
      { label: "Other", value: otherObservationValue },
    ],
    [observationTypeOptions]
  );

  const initialValues = useMemo(
    () => ({
      buildingInformationId: applicationId ?? "",
      scheduleId: scheduleId ?? "",
      observations: buildingObservationData?.observations?.map((item) => ({
        ...item,
        observationType: item.observationType
          ? { label: item.observationType.name, value: item.observationType.id }
          : "",
        detailObservationId: item?.detailObservation
          ? {
              label: item.detailObservation?.name,
              value: item.detailObservation?.id,
            }
          : {
              label: "Other",
              value: otherObservationValue,
            },
        tenantId: item.floorWiseTenant?.id ?? "",
      })) ?? [
        {
          id: 0,
          observationType: "",
          otherObservationType: "",
          tenantId: "",
          detailObservationId: null,
          otherDetailObservation: null,
          observationPhotographs: [],
          fireSafetyUser: { roles },
        },
      ],
    }),
    [applicationId, scheduleId, buildingObservationData]
  );

  const submitBtnHandler = async (values, isDraft = false) => {
    try {
      const payload = {
        ...values,
        observations: values.observations.map((item) => ({
          id: item.id,
          observationType:
            item.otherObservationType ||
            (item.observationType ? item.observationType.label : ""),
          detailObservationId:
            item.detailObservationId.value === otherObservationValue
              ? null
              : item.detailObservationId.value,
          otherDetailObservation:
            item.otherDetailObservation === ""
              ? null
              : item.otherDetailObservation,
          tenantId: item.tenantId,
          observationPhotographs: item.observationPhotographs.map(
            ({ id, file, photographUrl }) => ({
              id: file ? 0 : id,
              file: file ?? "",
              photographUrl: photographUrl ?? "",
            })
          ),
        })),
        isDraft,
      };

      const formData = new FormData();
      generateFormData(formData, filterEmptyStrings(payload));
      await addBuildingObservation(formData);
      if (isDraft) {
        setIsSaveButtonClicked(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,
    validationSchema: Yup.object().shape({
      observations: Yup.array().of(
        Yup.object({
          observationType: Yup.mixed()
            .nullable()
            .test("isObservationTypeRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];

              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.observationType
              ) {
                return this.createError({
                  message: "Observation Type is required",
                });
              }
              return true;
            }),

          detailObservationId: Yup.mixed()
            .nullable()
            .test("isDetailObservationRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.detailObservationId
              ) {
                return this.createError({
                  message: "Detail observation is required",
                });
              }
              return true;
            }),

          otherDetailObservation: Yup.string()
            .nullable()
            .test("isOtherObsRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                current?.detailObservationId?.value === otherObservationValue &&
                !current?.otherDetailObservation
              ) {
                return this.createError({
                  message: "Observation Type is required",
                });
              }
              return true;
            }),
          tenantId: Yup.string()
            .nullable()
            .test("isTenantRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];

              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !(current.tenantId >= 0)
              ) {
                return this.createError({
                  message: "Tenant is required",
                });
              }
              return true;
            }),

          otherObservationType: Yup.string()
            .nullable()
            .test("isOtherObservationTypeSelectedOther", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                current?.observationType?.value === otherObservationValue &&
                !current.otherObservationType
              ) {
                return this.createError({
                  message: "Observation name is required",
                });
              }
              return true;
            }),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        await submitBtnHandler(values, false);
        handleClose();
      } catch (err) {}
    },
  });

  const { values, setFieldValue, setFieldTouched, handleSubmit } = formik;
  const { observations } = values;
  const handleAddNewObservation = (existingObservation) => {
    const newObservation = [
      ...existingObservation,
      {
        id: 0,
        observationType: "",
        otherObservationType: "",
        detailObservationId: null,
        otherDetailObservation: null,
        tenantId: "",
        observationPhotographs: [],
        fireSafetyUser: { roles },
      },
    ];

    setIsAddBtnClicked(true);
    setFieldValue("observations", newObservation);
    setFieldTouched(`observations[${newObservation.length - 1}]`, false, false);
  };

  const handleRemoveBuildingObservation = (index) =>
    setFieldValue(
      "observations",
      observations.filter((_, ind) => ind !== index)
    );

  const fileChangeHandler = (files, index) => {
    const isAllFileValid = files.every(
      (file) =>
        validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
        file.size < 10485760
    );

    if (!isAllFileValid) {
      toast.error("Please upload image and size should be less than 10 mb");
    } else {
      setFieldValue(`observations.${index}.observationPhotographs`, [
        ...observations[index].observationPhotographs,
        ...files.map((file) => ({
          id: uniqueId("doc__"),
          url: URL.createObjectURL(file),
          name: file.name,
          file: file,
        })),
      ]);
    }
  };

  const saveBtnHandler = () => {
    setIsSaveButtonClicked(true);
    submitBtnHandler(values, true);
  };

  const getDetailObservationOption = (observationTypeId) => {
    const matched =
      buildingObservationOptionData
        ?.filter((item) => item.observationTypeId === observationTypeId.value)
        .map((item) => ({
          label: item.name,
          value: item.id,
          observationTypeId: item.observationTypeId,
        })) || [];

    return [
      ...matched,
      {
        label: "Other",
        value: otherObservationValue,
        observationTypeId: observationTypeId.id,
      },
    ];
  };

  useEffect(() => {
    (async () => {
      try {
        if (scheduleId) {
          await getBuildingObservationDataById(scheduleId);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [scheduleId]);

  // When new observation added it scrolls to bottom to that new observation
  useEffect(() => {
    if (observations && isAddBtnClicked) {
      ref.current?.scrollIntoView();
      setIsAddBtnClicked(false);
    }
  }, [observations]);

  useEffect(() => {
    (async () => {
      try {
        await getDetailObservationOptionById();
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  return (
    <>
      <Backdrop
        open={
          isBuildingObservationDataFetching ||
          isBuildingObservationDataLoading ||
          isBuildingDetailObservationDataLoading ||
          isBuildingDetailObservationDataFetching
        }
      />
      <Grid
        item
        xs={12}
        className="border ps-3 pe-2 pb-2 pt-0 mt-2"
        sx={{
          mb: 3,
          maxHeight: "594px",
          overflow: "auto",
        }}
      >
        {!!observations.length &&
          observations.map(
            (
              {
                observationPhotographs,
                observationType,
                detailObservationId,
                isApproved,
                fireSafetyUser: {
                  roles: currentRoles = [],
                  firstName,
                  lastName,
                } = {},
              },
              index
            ) => {
              const isDisabled =
                ((!isAuthorizedUser(roles, currentRoles) ||
                  !currentRoles.length) &&
                  !roles.includes(FIRE_EMERGENCY_CHIEF_FIRE_OFFICER)) ||
                isApproved;

              return (
                <Grid
                  key={index}
                  container
                  columnSpacing={1}
                  rowSpacing={1}
                  className="border p-2 pe-3 mt-2"
                  sx={{ overflow: "auto" }}
                  ref={observations.length - 1 === index ? ref : null}
                >
                  {!!firstName && (
                    <Grid xs={12} className="ms-2 mb-1">
                      <Typography>
                        <b>{t("Observation Added By")}</b> : {firstName}{" "}
                        {lastName}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Observation Type") + "*"}
                      name={`observations.${index}.observationType`}
                      formik={formik}
                      options={updatedObservationTypeOptions || []}
                      value={observations?.[index]?.observationType ?? ""}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      disabled={isDisabled}
                      onChange={(_, option) => {
                        setFieldValue(
                          `observations.${index}.observationType`,
                          option
                        );
                        setFieldValue(
                          `observations.${index}.otherObservationType`,
                          ""
                        );
                        setFieldValue(
                          `observations.${index}.detailObservationId`,
                          ""
                        );
                      }}
                    />
                  </Grid>

                  {observationType?.value === otherObservationValue && (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <InputFieldText
                        label={t("Enter Observation Name") + "*"}
                        name={`observations.${index}.otherObservationType`}
                        formik={formik}
                        disabled={isDisabled}
                        placeholder="Enter Observation Name"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Detail Observation") + "*"}
                      name={`observations.${index}.detailObservationId`}
                      value={observations?.[index]?.detailObservationId ?? ""}
                      disableClearable={false}
                      disabled={isDisabled}
                      onChange={(_, option) => {
                        setFieldValue(
                          `observations.${index}.detailObservationId`,
                          option ?? ""
                        );
                        setFieldValue(
                          `observations.${index}.otherDetailObservation`,
                          ""
                        );
                      }}
                      formik={formik}
                      options={
                        getDetailObservationOption(observationType) || []
                      }
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Select Tenant") + "*"}
                      name={`observations.${index}.tenantId`}
                      disableClearable={false}
                      disabled={isDisabled}
                      onChange={(_, option) => {
                        setFieldValue(
                          `observations.${index}.tenantId`,
                          option?.value ?? ""
                        );
                      }}
                      formik={formik}
                      options={tenantOptions || []}
                      getOptionLabel={(option) => {
                        const floor = option.floorName?.trim();
                        return floor
                          ? `${option.name} - ${floor}`
                          : option.name;
                      }}
                      getOptionValue={(option) => option.id}
                    />
                  </Grid>

                  <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={2}
                    sx={{ pl: 1 }}
                  >
                    <Grid item xs={12} className="mb-3 ms-1 mt-1">
                      <div className="d-flex gap-3 flex-wrap">
                        {!!observationPhotographs.length &&
                          observationPhotographs.map(
                            (
                              { photographUrl, url, name, id: currentId },
                              inx
                            ) => (
                              <div className="image-container" key={inx}>
                                <a
                                  href={
                                    photographUrl
                                      ? getImageUrl(photographUrl)
                                      : url
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Box
                                    component="img"
                                    className="image d-block m-auto"
                                    alt="document image"
                                    src={
                                      getFileExtension(
                                        (photographUrl
                                          ? photographUrl
                                          : name
                                        ).toLowerCase()
                                      ) === "pdf"
                                        ? pdfImg
                                        : url ?? getImageUrl(photographUrl)
                                    }
                                  />
                                </a>
                                {!isDisabled && (
                                  <i
                                    className="ri-close-circle-line fs-3 close-btn"
                                    onClick={() => {
                                      setFieldValue(
                                        `observations.${index}.observationPhotographs`,
                                        observationPhotographs.filter(
                                          ({ id }) => id !== currentId
                                        )
                                      );
                                    }}
                                  />
                                )}
                                <Tooltip title={name}>
                                  <DocumentName fontSize={13} variant="body2">
                                    {name}
                                  </DocumentName>
                                </Tooltip>
                              </div>
                            )
                          )}

                        <Box
                          component="label"
                          className="m-0 p-0 cursor-pointer"
                        >
                          <UploadImage isDisabled={isDisabled}>
                            <AddIcon
                              sx={{
                                color: !isDisabled ? "black" : "#dddddd",
                              }}
                            />
                          </UploadImage>
                          <input
                            type="file"
                            accept="image pdf/*"
                            disabled={isDisabled}
                            onChange={(e) => {
                              fileChangeHandler(
                                Array.from(e.target.files),
                                index
                              );
                              e.target.value = null;
                            }}
                            multiple
                            hidden
                          />
                        </Box>
                      </div>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <InputFieldText
                      label={t("Add Detail Observation") + "*"}
                      name={`observations.${index}.otherDetailObservation`}
                      value={observations[index]?.otherDetailObservation ?? ""}
                      disabled={
                        isDisabled ||
                        detailObservationId?.value !== otherObservationValue
                      }
                      InputLabelProps={{ shrink: true }}
                      formik={formik}
                      placeholder="Enter Detail Observation"
                      multiline
                      rows={2}
                    />
                  </Grid>

                  {!isDisabled && (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Button
                        color="error"
                        size="small"
                        sx={{
                          minWidth: "unset",
                          border: "1px solid",
                          height: "40px",
                          width: "100px",
                          mb: 2,
                        }}
                        onClick={() => handleRemoveBuildingObservation(index)}
                      >
                        <strong className="d-flex justify-content-center">
                          <DeleteOutlineSharpIcon fontSize="small" />
                          {t("Remove")}
                        </strong>
                      </Button>
                    </Grid>
                  )}
                </Grid>
              );
            }
          )}
      </Grid>

      <Box className="d-flex justify-content-between flex-wrap gap-2">
        <Button
          color="primary"
          sx={{
            minWidth: "unset",
            textTransform: "capitalize",
          }}
          disabled={
            isObservationNoticeSend ||
            buildingObservationData?.scheduleStatus === 3
          }
          variant="outlined"
          onClick={() => handleAddNewObservation(observations)}
        >
          <strong>
            <span>+</span> {t("Add Observation")}
          </strong>
        </Button>

        <div className="d-flex align-items-center gap-1">
          <Button
            size="medium"
            variant="outlined"
            type="submit"
            onClick={saveBtnHandler}
            disabled={isLoading && isSaveButtonClicked}
          >
            {t("Save")}
            {isLoading && isSaveButtonClicked && <CircularProgress size={20} />}
          </Button>

          <Button
            size="medium"
            variant="contained"
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading && !isSaveButtonClicked}
          >
            {t("Submit")}
            {isLoading && !isSaveButtonClicked && (
              <CircularProgress size={20} />
            )}
          </Button>
        </div>
      </Box>
    </>
  );
};

FireSafetyBuildingObservation.propTypes = {
  t: PropTypes.func,
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  scheduleId: PropTypes.number,
  isObservationNoticeSend: PropTypes.bool,
};

export default withTranslation()(FireSafetyBuildingObservation);
